import React, { useCallback, useContext } from 'react';
import { TextField, Box } from '@mui/material';
import SelectOrgChartHr from 'routes/HumanResourcesModule/PayState/PayStateHome/components/PayStateModal/SelectOrgChartHr';
import { SimulationTypeEnum } from 'routes/HumanResourcesModule/Simulation/helpers/constants';
import Loading from 'components/Loading';
import { useTranslation } from 'react-i18next';
import ModalHeader from 'ui/Modals/ModalHeader';
import SimulationInitMonthSelector from './SimulationInitMonthSelector';
import SimulationTypeSelector from './SimulationTypeSelector';
import OptionalFilterSelector from 'routes/HumanResourcesModule/Simulation/SimulationHome/components/SimulationModal/components/OptionalFilterSelector/OptionalFilterSelector';
import Flex from 'ui/Flex/Flex';
import { EtablissementAndPayBudgetContext } from 'routes/HumanResourcesModule/components/contexts/HumanResourcesContext';

export type SimulationFormFormState = {
    title: string;
    selectedMonth: string;
    selectedType: SimulationTypeEnum;
    isEtablissementsFilterActive: boolean;
    selectedOrgChartHrsSoftwareIds: string[];
    selectedEtablissementSoftwareIds: string[];
    isPayBudgetFilterActive: boolean;
    selectedPayBudgetSoftwareIds: string[];
    isFormValid: boolean;
};

type Props = {
    formState: SimulationFormFormState;
    setFormState: React.Dispatch<React.SetStateAction<SimulationFormFormState>>;
    isLoading: boolean;
    title?: string;
    subtitle?: string;
    description?: string;
    onOrgChartHrsChange?: (selectedOrgChartHrsSoftwareIds: string[]) => void;
};

const SimulationForm = ({
    formState,
    setFormState,
    title,
    subtitle,
    description,
    isLoading,
    onOrgChartHrsChange,
}: Props) => {
    const { t } = useTranslation();

    const onInputChange = useCallback(
        (name: keyof SimulationFormFormState, value: unknown) => {
            setFormState({ ...formState, [name]: value });
        },
        [JSON.stringify(formState), setFormState],
    );
    const { payBudget, etablissement } = useContext(
        EtablissementAndPayBudgetContext,
    );
    return (
        <>
            <ModalHeader
                title={title}
                subtitle={subtitle}
                description={description}
            />
            {isLoading ? (
                <Loading />
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2em',
                    }}
                >
                    <TextField
                        sx={{ marginTop: '1em' }}
                        label={t('humanResources.simulation.simulationTitle')}
                        value={formState.title}
                        required
                        onChange={(e) => onInputChange('title', e.target.value)}
                    />
                    <SimulationInitMonthSelector
                        value={formState.selectedMonth}
                        onChange={(e) =>
                            onInputChange('selectedMonth', e.target.value)
                        }
                    />
                    <SimulationTypeSelector
                        value={formState.selectedType}
                        onChange={(value) =>
                            onInputChange('selectedType', value)
                        }
                    />
                    <SelectOrgChartHr
                        selectedOrgChartHrsSoftwareIds={
                            formState.selectedOrgChartHrsSoftwareIds
                        }
                        setSelectedOrgChartHrsSoftwareIds={(value) => {
                            onInputChange(
                                'selectedOrgChartHrsSoftwareIds',
                                value,
                            );
                            if (onOrgChartHrsChange) {
                                onOrgChartHrsChange(value);
                            }
                        }}
                    />
                    <Flex direction="column" gap="1em">
                        <OptionalFilterSelector
                            field="etablissement"
                            data={etablissement ?? []}
                            isFilterActive={
                                formState.isEtablissementsFilterActive
                            }
                            setIsFilterActive={(value: boolean) => {
                                onInputChange(
                                    'isEtablissementsFilterActive',
                                    value,
                                );
                            }}
                            selectedItemIds={
                                formState.selectedEtablissementSoftwareIds
                            }
                            setSelectedItemIds={(value: string[]) => {
                                onInputChange(
                                    'selectedEtablissementSoftwareIds',
                                    value,
                                );
                                if (onOrgChartHrsChange) {
                                    onOrgChartHrsChange(value);
                                }
                            }}
                        />
                        <OptionalFilterSelector
                            field="pay_budget"
                            data={payBudget ?? []}
                            isFilterActive={formState.isPayBudgetFilterActive}
                            setIsFilterActive={(value: boolean) => {
                                onInputChange('isPayBudgetFilterActive', value);
                            }}
                            selectedItemIds={
                                formState.selectedPayBudgetSoftwareIds
                            }
                            setSelectedItemIds={(value: string[]) => {
                                onInputChange(
                                    'selectedPayBudgetSoftwareIds',
                                    value,
                                );
                                if (onOrgChartHrsChange) {
                                    onOrgChartHrsChange(value);
                                }
                            }}
                        />
                    </Flex>
                </Box>
            )}
        </>
    );
};

export default SimulationForm;
